import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, MapPin, Trash2, Edit2, Save, X, Users, TrendingUp, TrendingDown, Minus, Radio, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { turkeyData, provinces } from "@/data/turkeyData";

const EMPTY = {
  name: "", address: "", province: "", district: "", neighborhood: "",
  lat: "", lng: "", phone: "", email: "", scholarship_quota: "", active: true,
};

const TREND_META = {
  up: { icon: TrendingUp, cls: "text-success", label: "artıyor" },
  down: { icon: TrendingDown, cls: "text-destructive", label: "azalıyor" },
  flat: { icon: Minus, cls: "text-muted-foreground", label: "sabit" },
};

export default function SchoolCampuses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [occupancy, setOccupancy] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user?.school_id) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`${V1}/schools/${user.school_id}/campuses`);
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOccupancy = async () => {
    if (!user?.school_id) return;
    try {
      const r = await api.get(`${V1}/schools/${user.school_id}/campuses/occupancy`);
      const map = {};
      (r.data || []).forEach((o) => { map[o.campus_id] = o; });
      setOccupancy(map);
    } catch { /* silent — live widget is best-effort */ }
  };

  useEffect(() => {
    load();
    loadOccupancy();
    // "Canlı" refresh — polls every 20s so quota/trend stay current without a full reload.
    const iv = setInterval(loadOccupancy, 20000);
    return () => clearInterval(iv);
  }, [user?.school_id]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      ...EMPTY,
      ...c,
      lat: c.lat ?? "",
      lng: c.lng ?? "",
      scholarship_quota: c.scholarship_quota ?? "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Kampüs adı gerekli"); return; }
    setBusy(true);
    const payload = {
      ...form,
      lat: form.lat === "" ? null : parseFloat(form.lat),
      lng: form.lng === "" ? null : parseFloat(form.lng),
      scholarship_quota: form.scholarship_quota === "" ? null : parseInt(form.scholarship_quota),
      education_levels: form.education_levels || [],
      facilities: form.facilities || [],
    };
    try {
      if (editingId) {
        await api.patch(`${V1}/campuses/${editingId}`, payload);
        toast.success("Kampüs güncellendi");
      } else {
        await api.post(`${V1}/schools/${user.school_id}/campuses`, payload);
        toast.success("Kampüs eklendi");
      }
      cancelEdit();
      await load();
      await loadOccupancy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kampüsü silmek istiyor musun?")) return;
    try {
      await api.delete(`${V1}/campuses/${id}`);
      toast.success("Kampüs silindi");
      await load();
      await loadOccupancy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  if (loading) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="school-campuses-page">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Building2 className="h-7 w-7 text-primary" /> Kampüsler</h1>
        <p className="text-muted-foreground text-sm mt-1">Birden fazla yerleşkeniz varsa her biri için ayrı adres, koordinat, kontenjan ve iletişim bilgisi ekleyin. Kampüs bilgisi başvuru akışında ve okul detay sayfasında gösterilir.</p>
      </div>

      <div className="p-6 rounded-xl border border-border bg-card space-y-3">
        <div className="font-medium flex items-center gap-2">
          {editingId ? <Edit2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {editingId ? "Kampüs Düzenle" : "Yeni Kampüs Ekle"}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input placeholder="Kampüs adı (Merkez, Bahçelievler…)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campus-name-input" />
          
          <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v, district: "" })}>
            <SelectTrigger data-testid="campus-province-input"><SelectValue placeholder="İl Seçiniz" /></SelectTrigger>
            <SelectContent>
              {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v })} disabled={!form.province}>
            <SelectTrigger data-testid="campus-district-input"><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
            <SelectContent>
              {form.province && turkeyData[form.province] ? turkeyData[form.province].map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              )) : null}
            </SelectContent>
          </Select>

          <Input placeholder="Mahalle" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} data-testid="campus-neighborhood-input" />
          <Input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="campus-phone-input" />
          <Input placeholder="E-posta" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="campus-email-input" />
          <Input type="number" step="any" placeholder="Enlem (ör: 39.9250)" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="campus-lat-input" />
          <Input type="number" step="any" placeholder="Boylam (ör: 32.8660)" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="campus-lng-input" />
          <Input type="number" min="0" placeholder="Bursluluk kontenjanı (ör: 50)" value={form.scholarship_quota} onChange={(e) => setForm({ ...form, scholarship_quota: e.target.value })} data-testid="campus-quota-input" />
        </div>
        <Textarea placeholder="Açık adres" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="campus-address-input" />
        <div className="flex gap-2 justify-end">
          {editingId && <Button variant="outline" onClick={cancelEdit}><X className="h-4 w-4" /> İptal</Button>}
          <Button onClick={save} disabled={busy} data-testid="campus-save-btn"><Save className="h-4 w-4" /> {editingId ? "Güncelle" : "Ekle"}</Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
            Henüz kampüs eklenmedi. Yukarıdaki formdan ekleyin.
          </div>
        )}
        {items.map((c) => {
          const occ = occupancy[c.id];
          const trend = occ ? TREND_META[occ.trend] : null;
          return (
            <div key={c.id} className="p-4 rounded-xl border border-border bg-card flex flex-col md:flex-row md:items-start justify-between gap-3" data-testid={`campus-item-${c.id}`}>
              <div className="flex-1">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" /> {[c.district, c.province].filter(Boolean).join(", ")}
                  {c.lat && c.lng && <span className="ml-2 opacity-70">· {c.lat}, {c.lng}</span>}
                </div>
                {c.address && <div className="text-xs mt-1">{c.address}</div>}
                {c.phone && <div className="text-xs mt-1">📞 {c.phone}</div>}
                {!c.active && <div className="text-xs mt-1 text-destructive">Yayında değil</div>}
              </div>

              {occ && (
                <div
                  className={`flex items-center gap-4 px-4 py-2 rounded-lg border shrink-0 ${
                    occ.remaining != null && occ.remaining < 0 ? "border-destructive/50 bg-destructive/5" : "border-border bg-muted/30"
                  }`}
                  data-testid={`campus-occupancy-${c.id}`}
                >
                  <div className="flex items-center gap-1.5 text-xs text-accent">
                    <Radio className="h-3 w-3 animate-pulse" /> Canlı
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 justify-center"><Users className="h-3 w-3" /> Kalan Kontenjan</div>
                    {occ.remaining != null && occ.remaining < 0 ? (
                      <div className="flex items-center gap-1 text-destructive" data-testid={`campus-overbooked-${c.id}`}>
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-display font-bold text-lg" data-testid={`campus-remaining-${c.id}`}>{occ.remaining}</span>
                      </div>
                    ) : (
                      <div className="font-display font-bold text-lg" data-testid={`campus-remaining-${c.id}`}>
                        {occ.remaining != null ? occ.remaining : "—"}
                        {occ.quota != null && <span className="text-xs font-normal text-muted-foreground"> / {occ.quota}</span>}
                      </div>
                    )}
                    {occ.remaining != null && occ.remaining < 0 && (
                      <div className="text-[10px] text-destructive font-medium mt-0.5">{Math.abs(occ.remaining)} kişi kontenjan aştı</div>
                    )}
                  </div>
                  {trend && (
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Bu Hafta</div>
                      <div className={`font-display font-bold text-lg flex items-center gap-1 justify-center ${trend.cls}`}>
                        <trend.icon className="h-4 w-4" /> {occ.this_week}
                      </div>
                      <div className="text-[10px] text-muted-foreground">tercih {trend.label}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => startEdit(c)} data-testid={`campus-edit-${c.id}`}><Edit2 className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid={`campus-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
